import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import _ from 'lodash'
import format from 'date-fns/format'
import addMinutes from 'date-fns/add_minutes';
import addHours from 'date-fns/add_hours';
import subHours from 'date-fns/sub_hours';
import { getExcelsDetail,getCompany,getUserAll, getTeamByCompany, invalidateCompanyCache } from './core/cacheService'
import { apiNodeRequest } from './core/main'
import countriesAndTimezone from 'countries-and-timezones';
import { getCompanyTimezone, toCompanyDate } from './utils/companyTimezone'
import {
  resolveActiveExcelSaveAction,
  shouldSkipExcelsCompanyWrite,
} from './utils/peerFeedbackExcelsSave'

let pendingExcelsFetch: Promise<void> | null = null

class Convert {
  time: any;
  constructor (message: any) {
      this.time = message;
  }
  convertTime() : any {
    // Accepts "h:mm A", "HH:mm", Date, and safely falls back.
    let timeC = this.time;
    if (timeC instanceof Date) {
      return format(timeC, 'HH:mm');
    }

    timeC = timeC === undefined || timeC === null ? '' : String(timeC).trim();
    if (!timeC || timeC === 'Invalid Date') {
      return '00:00';
    }

    const timeWithPeriod = timeC.match(/^(\d{1,2}):(\d{1,2})(?:\s*([AP]M))?$/i);
    if (!timeWithPeriod) {
      return '00:00';
    }

    let hours = Number(timeWithPeriod[1]);
    let minutes = Number(timeWithPeriod[2]);
    const AMPM = (timeWithPeriod[3] || '').toUpperCase();

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return '00:00';
    }
    if (minutes < 0 || minutes > 59 || hours < 0 || hours > 23) {
      return '00:00';
    }
    if(AMPM == "PM" && hours<12) hours = hours+12;
    if(AMPM == "AM" && hours==12) hours = hours-12;
    let sHours = hours.toString();
    let sMinutes = minutes.toString();
    if(hours<10) sHours = "0" + sHours;
    if(minutes<10) sMinutes = "0" + sMinutes;
    return sHours + ":" + sMinutes;
  }
}

const FEEDBACK_ROLLUP_SCOPES: string[] = ['direct', 'default', 'unlimited'];

const isGiftCrossLocationRewardsEnabled = (value: any) => value === 'Y';

const getUsersArray = (users: any) => {
  if (!users) return []
  return Array.isArray(users) ? users : Object.values(users)
}

const normalizeCountryCode = (value: any) => {
  return value ? String(value).trim().toUpperCase() : ''
}

const getCountryName = (code: string) => {
  const countries = countriesAndTimezone.getAllCountries()
  return countries[code] ? countries[code].name : code
}

const buildCountryOptions = (users: any) => {
  const grouped: any = {}
  for (const user of getUsersArray(users)) {
    const code = normalizeCountryCode((user as any).country)
    if (!code) continue
    if (!grouped[code]) {
      grouped[code] = {
        code,
        id: code,
        name: getCountryName(code),
        membercount: 0,
        users: [],
      }
    }
    grouped[code].membercount += 1
    grouped[code].users.push((user as any).email)
  }
  return _.orderBy(Object.values(grouped), ['name'], ['asc'])
}

const buildCohortOptions = (cohorts: any) => {
  const options: Array<any> = []
  for (const id in cohorts || {}) {
    const cohort = cohorts[id] || {}
    const users = getUsersArray(cohort.users).filter(Boolean)
    options.push({
      id,
      name: cohort.name || '',
      membercount: users.length,
      users,
    })
  }
  return _.orderBy(options, ['name'], ['asc'])
}

const applyCompanyData = (companyData: any) => {
  // @see https://trello.com/c/DqT0uGfd/1850-power-ups-as-a-company-i-want-to-customize-my-performance-review-schedule-so-that-it-aligns-with-my-current-processes
  // default values
  if (!companyData.performance_review) {
    companyData.performance_review = {
      enabled: true,
      overdue: 2,
      pending: 1,
      ai_review_feedback: true,
      reset_completed_monthly: false,
    }
  }
  // TODO: Backend team needs to add 'enabled' field to company settings schema in Firebase
  if(companyData.performance_review.enabled == undefined){
    companyData.performance_review.enabled = true
  }
  if(companyData.performance_review.ai_review_feedback == undefined){
    companyData.performance_review.ai_review_feedback = true
  }
  if(companyData.reset_completed_monthly){
    companyData.performance_review.reset_completed_monthly = companyData.reset_completed_monthly
  }
  if (!companyData.limit) {
    companyData.limit = {
      openend: 3,
    }
  }

  // Battle Stadium Fair Play Boost — off by default. Normalize legacy "Y"
  // values so the Settings toggle always works with a boolean while preserving
  // any other scoring configuration stored on the company record.
  const battleStadiumScoring = companyData.battle_stadium_scoring || {}
  companyData.battle_stadium_scoring = {
    ...battleStadiumScoring,
    fair_play_boost_enabled:
      battleStadiumScoring.fair_play_boost_enabled === true ||
      battleStadiumScoring.fair_play_boost_enabled === 'Y',
  }

  // Gem Exchange — off by default; admins opt in per company
  if (!companyData.gem_exchange) {
    companyData.gem_exchange = {
      enabled: false,
      tiers: [],
    }
  }
  if (companyData.gem_exchange.enabled === undefined) {
    companyData.gem_exchange.enabled = false
  }
  if (!Array.isArray(companyData.gem_exchange.tiers)) {
    companyData.gem_exchange.tiers = []
  }

  // Performance Conversations (KR update reflections) — off by default; admins opt in
  if (!companyData.performance_conversations) {
    companyData.performance_conversations = {
      enabled: false,
      min_days_between: 7,
      ai_timeline_summary: true,
    }
  }
  if (companyData.performance_conversations.enabled === undefined) {
    companyData.performance_conversations.enabled = false
  }
  if (!(Number(companyData.performance_conversations.min_days_between) > 0)) {
    companyData.performance_conversations.min_days_between = 7
  }
  if (companyData.performance_conversations.ai_timeline_summary === undefined) {
    companyData.performance_conversations.ai_timeline_summary = true
  }

  // Town Hall Agent
  if (!_.has(companyData, "townhall_agent")) {
    companyData.townhall_agent = false;
  }

  // Town Hall Theme
  if (!_.has(companyData, 'townhall_themes')) {
    companyData.townhall_themes = [
      { key: 'innovation', name: 'Innovation' },
      { key: 'work-life-balance', name: 'Work-Life Balance' },
      { key: 'team-building', name: 'Team Building' },
      { key: 'problem-solving', name: 'Problem Solving' },
      { key: 'communication', name: 'Communication' },
      { key: 'leadership', name: 'Leadership' },
      { key: 'creativity', name: 'Creativity' },
      { key: 'adaptability', name: 'Adaptability' },
      { key: 'empathy', name: 'Empathy' },
      { key: 'time-management', name: 'Time Management' },
    ];
  }

  // Email Notifications - BACKEND TODO: Use these settings to control email sending
  // All default to true (ON) - users can disable specific notifications via settings UI
  // Backend should check these boolean values before sending any notification emails
  if (!_.has(companyData, 'email_notifications')) {
    companyData.email_notifications = {
      managerRewardRedemption: true,        // Manager gets email when team member redeems reward
      managerPeerFeedbackOutstanding: true, // Manager gets email when team member gets outstanding feedback
    };
  }
  if (!_.has(companyData, 'activity_feed_use_nickname')) {
    companyData.activity_feed_use_nickname = true;
  }
  if (!_.has(companyData, 'activity_feed_show_rewards')) {
    companyData.activity_feed_show_rewards = true;
  }
  if (!_.has(companyData, 'gift_cross_location')) {
    companyData.gift_cross_location = 'N';
  }
  companyData.giftCrossLocationRewards_checked = isGiftCrossLocationRewardsEnabled(companyData.gift_cross_location);
  if(!companyData.report_monthlyhr){
    companyData.report_monthlyhr = 'Y'
  }
  if(!companyData.report_activity_manager){
    companyData.report_activity_manager = 'Y'
  }
  if (!FEEDBACK_ROLLUP_SCOPES.includes(companyData.feedback_rollup_scope)) {
    companyData.feedback_rollup_scope = 'default';
  }
}

const hasOwn = (obj: any, key: string) => {
  return Object.prototype.hasOwnProperty.call(obj || {}, key)
}

const buildLanguageTemplate = (languageList: Array<any>) => {
  const languageTemplate: any = {}
  for (const lang of languageList || []) {
    if (!languageTemplate[lang.code]) {
      languageTemplate[lang.code] = ''
    }
  }
  return languageTemplate
}

const normalizeHashtagStatus = (value: any) =>
  value === true || value === 'Y' || value === 'true'

const normalizeRecognitionValue = (item: any, key: string, order: number) => {
  const rawThai = hasOwn(item.names, 'th')
    ? item.names.th
    : item.name_th !== undefined
      ? item.name_th
      : item.labelTh

  const labelTh = !rawThai || rawThai === item.name ? '#' : rawThai

  return {
    ...item,
    names: {
      ...(item.names || {}),
      en: item.name,
      th: labelTh,
    },
    particular: { ...(item.particular || {}) },
    value: key,
    label: item.name,
    labelTh,
    order: item.order ? item.order : order,
  }
}

const normalizeExcelNames = (item: any, languageTemplate: any) => {
  const names = {
    ..._.clone(languageTemplate),
    ...(item.names || {}),
  }

  names.en = item.name

  if (!hasOwn(names, 'th')) {
    names.th = item.name_th && item.name_th !== item.name ? item.name_th : ''
  } else if (names.th === item.name) {
    names.th = ''
  }

  for (const lang in languageTemplate) {
    if (lang !== 'en' && names[lang] === item.name) {
      names[lang] = ''
    }
  }

  return names
}

export default {
    namespaced: true,
    state: {
      settingData: {
        starttime: '',
        report_dailyboss_time: '',
        report_weeklyboss_time: '',
        report_monthlyboss_time: '',
        report_monthlyhr: true,
        report_activity_manager: true,
        activity_feed_use_nickname: true,
        activity_feed_show_rewards: true,
        giftCrossLocationRewards_checked: false,
        feedback_rollup_scope: 'default',
        // Pre-declared so Vue 2 registers reactive deps on first render
        // (before Firebase load populates them). Do not remove.
        recognitionValue_checked: false,
        medalsForLeaders_checked: false,
        peerFeedbackInteractions_checked: false,
        happilyAssistAccess_checked: false,
        happilyVillageAccess_checked: false,
        townHallAgent_checked: false,
        create_topics: false,
        demoMode_checked: false,
        performance_review: {
          enabled: true,
          overdue: 2,
          pending: 1,
          ai_review_feedback: true,
          reset_completed_monthly: false,
        },
        performance_conversations: {
          enabled: false,
          min_days_between: 7,
          ai_timeline_summary: true,
        },
      },
      excels:[],
      recognitionValue:[],
      hashTagId:'',
      oldRecognitionValue:[],
      settingDataMock: {},
      settingOldDataMock:[],
      multiLang:false,
      oldExcels:[],
      language:[],
      organization1level: [],
      memberinteam: [],
      memberInteamAll_master: [],
      memberInteamAll: [],
      cohortOptions: [],
      cohortOptionsAll: [],
      countryOptions: [],
      countryOptionsAll: [],
      allUser: [],
      member: [],
      presetlist:[],
      presetlistAll:[],
      loading_preset:false,
      townHallThemes: [],
      excels_detail:[],
    },
    mutations: {
      setDataSetting(state:any, result:any) {
        state.settingData = result
      },
      setExcels(state:any, result:any) {
        state.excels = result
      },
      setRecognitionValue(state:any, result:any) {
        state.recognitionValue = result
      },
      setOldRecognition(state:any, result:any) {
        state.oldRecognitionValue = result
      },
      sethashTagId(state:any, result:any) {
        state.hashTagId = result
      },
      setSettingDataMock(state:any, result:any) {
        state.settingDataMock = result
      },
      setSettingOldDataMock(state:any, result:any) {
        state.settingOldDataMock = result
      },
      setMultiLang(state:any, result:boolean) {
        state.multiLang = result
      },
      setOldExcels(state:any, result:boolean) {
        state.oldExcels = result
      },
      setlanguage(state:any, result:any){
        state.language = result
      },
      setMultipleState(state: any, payload: any) {
        Object.assign(state, payload)
      },
      setTownHallThemes(state:any, result:any) {
        state.townHallThemes = result
      },
      setExcelsDetail(state:any, result:any) {
        state.excels_detail = result
      }
    },
    actions: {
      async checkEngOnly({ commit, rootState, state, dispatch }: any){
        const LANG_FALLBACK:any = {
          'th': 'Thai',
          'ms': 'Bahasa Malaysia',
          'zh-HK': 'Chinese (Traditional, Hong Kong)',
          'pt-br': 'Brazilian Portuguese',
          'my': 'Burmese'
        }
        const materlanguage = (await firebase.database().ref(`/language`).once('value')).val() || {}

        const language: { code: string; text: string }[] = [{code:"en",text:"English (Default)"}]

        if (!state.language || state.language.length === 0) {
          commit('setlanguage', language)
        }

        let allUser = rootState.main && rootState.main.allUser
        if (!allUser || Object.keys(allUser).length === 0) {
          try {
            allUser = await getUserAll(rootState.authen.user.company) || {}
          } catch (e) {
            console.error('[checkEngOnly] getUserAll fallback failed', e)
            allUser = {}
          }
        }

        const arr = _.groupBy(allUser, function (b:any) {
          return b.language
        })

        if (Object.keys(arr).length >= 1) {
          for (const code in arr) {
            const text = materlanguage[code] ? materlanguage[code].name.toString() : (LANG_FALLBACK[code] || '')
            if (code !== 'en' && code !== 'undefined' && text) {
              language.push({ code, text })
            }
          }
          commit('setMultiLang', language.length > 1)
          commit('setlanguage', language)
          dispatch('getExcelsByCompany')
        }
      },
      async getDataSetting({commit,dispatch,state}:any, user:any){
        const dateNow = new Date()
        const timezone = await getCompanyTimezone(user.company)
        const companyNow = toCompanyDate(new Date(), timezone)
        let recognitionValue:Array<any>=[]
        let oldRecognitionValue:Array<any>=[]
        const companyData = _.cloneDeep(await getCompany(user.company))
        if(companyData.timezone){
          const setlengthTimezone = [];
          setlengthTimezone['7'] = [4, 4.5, 5, 5.5, 5.75, 6, 6.5, 7, 8, 9, 9.5, 10];
          setlengthTimezone['-7'] = [-4, -5, -6, -7, -8, -9, -10];
          setlengthTimezone['0'] = [-3.5, -3, -2, -1, 0, 1, 2, 3, 3.5];
          let globalTime = 0
          if (setlengthTimezone['7'].indexOf( parseFloat(companyData.timezone)) !== -1)
              globalTime = 7;
          if (setlengthTimezone['-7'].indexOf( parseFloat(companyData.timezone)) !== -1)
              globalTime = -7;
          if (setlengthTimezone['0'].indexOf( parseFloat(companyData.timezone)) !== -1)
              globalTime = 0;

          const diffTimezone = companyData.timezone - globalTime
          if(companyData.starttime){
            let DailyPulse;
            // if(companyData.starttime_show){
            //   DailyPulse = companyData.starttime_show.split(":")
            // }else{
              DailyPulse = companyData.starttime.split(":")
            // }
            companyData.starttime = format(addHours(dateNow.setHours(DailyPulse[0], DailyPulse[1]),diffTimezone), 'h:mm A')
          }

          if(companyData.reminder_time){
            const DailyReminder = companyData.reminder_time.split(":")
            companyData.reminder_time = format(addHours(dateNow.setHours(DailyReminder[0], DailyReminder[1]),diffTimezone), 'h:mm A')
          }

          if(companyData.report_dailyboss_time){
            const DailyInsights = companyData.report_dailyboss_time.split(":")
            companyData.report_dailyboss_time = format(addHours(dateNow.setHours(DailyInsights[0], DailyInsights[1]),diffTimezone), 'h:mm A')
          }
  
          if(companyData.report_weeklyboss_time){
            const WeeklyInsights = companyData.report_weeklyboss_time.split(":")
            companyData.report_weeklyboss_time = format(addHours(dateNow.setHours(WeeklyInsights[0], WeeklyInsights[1]),diffTimezone), 'h:mm A')
          }
          if(companyData.report_monthlyboss_time){
            const monthlyInsights = companyData.report_monthlyboss_time.split(":")
            companyData.report_monthlyboss_time = format(addHours(dateNow.setHours(monthlyInsights[0], monthlyInsights[1]),diffTimezone), 'h:mm A')
          }
        }else{
          if(companyData.starttime){
            let DailyPulse;
            // if(companyData.starttime_show){
            //   DailyPulse = companyData.starttime_show.split(":")
            // }else{
              DailyPulse = companyData.starttime.split(":")
            // }
            companyData.starttime = format(dateNow.setHours(DailyPulse[0], DailyPulse[1]), 'h:mm A')
          }
  
          if(companyData.reminder_time){
            const DailyReminder = companyData.reminder_time.split(":")
            companyData.reminder_time = format(dateNow.setHours(DailyReminder[0], DailyReminder[1]), 'h:mm A')
          }
  
          if(companyData.report_dailyboss_time){
            const DailyInsights = companyData.report_dailyboss_time.split(":")
            companyData.report_dailyboss_time = format(dateNow.setHours(DailyInsights[0], DailyInsights[1]), 'h:mm A')
          }
  
          if(companyData.report_weeklyboss_time){
            const WeeklyInsights = companyData.report_weeklyboss_time.split(":")
            companyData.report_weeklyboss_time = format(dateNow.setHours(WeeklyInsights[0], WeeklyInsights[1]), 'h:mm A')
          }
          if(companyData.report_monthlyboss_time){
            const monthlyInsights = companyData.report_monthlyboss_time.split(":")
            companyData.report_monthlyboss_time = format(dateNow.setHours(monthlyInsights[0], monthlyInsights[1]), 'h:mm A')
          }
        }
        
        if(companyData.medal){
          if(companyData.medal.Creativity){
            companyData.Creativity = companyData.medal.Creativity
            companyData.CreativityTH =companyData.medals?companyData.medals.Creativity? companyData.medals.Creativity.th==companyData.medals.Creativity.en?"":companyData.medals.Creativity.th:"":""
          }

          if(companyData.medal.Initiative){
            companyData.Initiative = companyData.medal.Initiative
            companyData.InitiativeTH = companyData.medals?companyData.medals.Initiative? companyData.medals.Initiative.th==companyData.medals.Initiative.en?"":companyData.medals.Initiative.th:"":""
          }

          if(companyData.medal.Kindness){
            companyData.Kindness = companyData.medal.Kindness
            companyData.KindnessTH = companyData.medals?companyData.medals.Kindness? companyData.medals.Kindness.th==companyData.medals.Kindness.en?"":companyData.medals.Kindness.th:"":""
          }

          if(companyData.medal.Strength){
            companyData.Strength = companyData.medal.Strength
            companyData.StrengthTH = companyData.medals?companyData.medals.Strength? companyData.medals.Strength.th==companyData.medals.Strength.en?"":companyData.medals.Strength.th:"":""
          }

          if(companyData.medal.Charisma){
            companyData.Charisma = companyData.medal.Charisma
            companyData.CharismaTH =companyData.medals?companyData.medals.Charisma? companyData.medals.Charisma.th==companyData.medals.Charisma.en?"":companyData.medals.Charisma.th:"":""
          }

          if(companyData.medal.Intelligence){
            companyData.Intelligence = companyData.medal.Intelligence
            companyData.IntelligenceTH =companyData.medals?companyData.medals.Intelligence? companyData.medals.Intelligence.th==companyData.medals.Intelligence.en?"":companyData.medals.Intelligence.th:"":""
          }

        }else{
          companyData.Creativity = "Creativity"
          companyData.Initiative = "Initiative"
          companyData.Kindness = "Kindness"
          companyData.Strength = "Strength"
          companyData.Charisma = "Charisma"
          companyData.Intelligence = "Intelligence"

        }
        if(!companyData.medals){
          companyData.CreativityTH = ""
          companyData.InitiativeTH = ""
          companyData.KindnessTH = ""
          companyData.StrengthTH = ""
          companyData.CharismaTH = ""
          companyData.IntelligenceTH = ""

        }
        if(companyData['holiday_tag'] === undefined){
          companyData['holiday_tag'] = new Array()
        }
        if(companyData['holiday_val'] === undefined){
          companyData['holiday_val'] = new Array()
        }
        await firebase.firestore().collection('holliday').where("companyid", "==", user.company).where("date",">=",format(companyNow, 'YYYY-MM-DD')).orderBy('date').get()
        .then(function (querySnapshot) {
            querySnapshot.forEach(function (doc) {
                const data = doc.data();
                let date_holiday = data['date'].split("-");

                companyData['holiday_tag'].push(format(data['date'], 'dddd, MMMM D, YYYY'))
                companyData['holiday_val'].push(new Date(`${date_holiday[0]}/${date_holiday[1]}/${date_holiday[2]}`))
            });
        });
        let count=0
        for(var value in companyData.hashtag){
          count++

          const normalizedRecognition = normalizeRecognitionValue(
            companyData.hashtag[value],
            value,
            count
          )

          recognitionValue.push(normalizedRecognition);
          oldRecognitionValue.push(_.cloneDeep(normalizedRecognition));
        }
        // console.log('recognitionValue',recognitionValue)
          companyData.report_monthly_insights= companyData.report_monthlyboss?companyData.report_monthlyboss:"N"
          companyData.report_monthlyboss_time= companyData.report_monthlyboss_time
          companyData.happilyAssistAccess_checked= companyData.assist_access?companyData.assist_access:false
          companyData.medalsForLeaders_checked= companyData.leadership=="Y"?true:false
          companyData.recognitionValue_checked= normalizeHashtagStatus(companyData.hashtag_status)
          companyData.peerFeedbackInteractions_checked= companyData.peer_feedback=="Y"?true:false
          companyData.happilyVillageAccess_checked = companyData.house_village=="Y"?true:false
          companyData.create_topics= companyData.create_topics=="Y"?true:false

        applyCompanyData(companyData);
        companyData.townHallAgent_checked = Boolean(companyData.townhall_agent);
        companyData.townhall_themes = companyData.townhall_themes;
        companyData.emailNotifications = companyData.email_notifications;
        companyData.report_monthlyhr = companyData.report_monthlyhr == 'Y' ? true : false;
        companyData.report_activity_manager = companyData.report_activity_manager == 'Y' ? true : false;
        commit('setTownHallThemes', companyData.townhall_themes);

        recognitionValue=_.orderBy(recognitionValue,['order'],["asc"])
        oldRecognitionValue=_.orderBy(oldRecognitionValue,['order'],["asc"])
        commit('setOldRecognition',oldRecognitionValue) 
        commit('setRecognitionValue',recognitionValue)
        //console.log('companyData',companyData)
        commit('setDataSetting',companyData)
      },
      async getoldDataSetting({commit,dispatch,state}:any, user:any){
        const dateNow = new Date()
        const timezone = await getCompanyTimezone(user.company)
        const companyNow = toCompanyDate(new Date(), timezone)
        let recognitionValue:Array<any>=[]
        let oldRecognitionValue:Array<any>=[]
        const companyData = _.cloneDeep(await getCompany(user.company))

        if(companyData.timezone){
          const setlengthTimezone = [];
          setlengthTimezone['7'] = [4, 4.5, 5, 5.5, 5.75, 6, 6.5, 7, 8, 9, 9.5, 10];
          setlengthTimezone['-7'] = [-4, -5, -6, -7, -8, -9, -10];
          setlengthTimezone['0'] = [-3.5, -3, -2, -1, 0, 1, 2, 3, 3.5];
          let globalTime = 0
          if (setlengthTimezone['7'].indexOf( parseFloat(companyData.timezone)) !== -1)
              globalTime = 7;
          if (setlengthTimezone['-7'].indexOf( parseFloat(companyData.timezone)) !== -1)
              globalTime = -7;
          if (setlengthTimezone['0'].indexOf( parseFloat(companyData.timezone)) !== -1)
              globalTime = 0;

          const diffTimezone = companyData.timezone - globalTime
          if(companyData.starttime){
            let DailyPulse;
            // if(companyData.starttime_show){
            //   DailyPulse = companyData.starttime_show.split(":")
            // }else{
              DailyPulse = companyData.starttime.split(":")
            // }
            companyData.starttime = format(addHours(dateNow.setHours(DailyPulse[0], DailyPulse[1]),diffTimezone), 'h:mm A')
          }

          if(companyData.reminder_time){
            const DailyReminder = companyData.reminder_time.split(":")
            companyData.reminder_time = format(addHours(dateNow.setHours(DailyReminder[0], DailyReminder[1]),diffTimezone), 'h:mm A')
          }

          if(companyData.report_dailyboss_time){
            const DailyInsights = companyData.report_dailyboss_time.split(":")
            companyData.report_dailyboss_time = format(addHours(dateNow.setHours(DailyInsights[0], DailyInsights[1]),diffTimezone), 'h:mm A')
          }
  
          if(companyData.report_weeklyboss_time){
            const WeeklyInsights = companyData.report_weeklyboss_time.split(":")
            companyData.report_weeklyboss_time = format(addHours(dateNow.setHours(WeeklyInsights[0], WeeklyInsights[1]),diffTimezone), 'h:mm A')
          }
          if(companyData.report_monthlyboss_time){
            const monthlyInsights = companyData.report_monthlyboss_time.split(":")
            companyData.report_monthlyboss_time = format(addHours(dateNow.setHours(monthlyInsights[0], monthlyInsights[1]),diffTimezone), 'h:mm A')
          }
        }else{
          if(companyData.starttime){
            let DailyPulse;
            // if(companyData.starttime_show){
            //   DailyPulse = companyData.starttime_show.split(":")
            // }else{
              DailyPulse = companyData.starttime.split(":")
            // }
            companyData.starttime = format(dateNow.setHours(DailyPulse[0], DailyPulse[1]), 'h:mm A')
          }
  
          if(companyData.reminder_time){
            const DailyReminder = companyData.reminder_time.split(":")
            companyData.reminder_time = format(dateNow.setHours(DailyReminder[0], DailyReminder[1]), 'h:mm A')
          }
  
          if(companyData.report_dailyboss_time){
            const DailyInsights = companyData.report_dailyboss_time.split(":")
            companyData.report_dailyboss_time = format(dateNow.setHours(DailyInsights[0], DailyInsights[1]), 'h:mm A')
          }
  
          if(companyData.report_weeklyboss_time){
            const WeeklyInsights = companyData.report_weeklyboss_time.split(":")
            companyData.report_weeklyboss_time = format(dateNow.setHours(WeeklyInsights[0], WeeklyInsights[1]), 'h:mm A')
          }
          if(companyData.report_monthlyboss_time){
            const monthlyInsights = companyData.report_monthlyboss_time.split(":")
            companyData.report_monthlyboss_time = format(dateNow.setHours(monthlyInsights[0], monthlyInsights[1]), 'h:mm A')
          }
        }
        
        if(companyData.medal){
          if(companyData.medal.Creativity){
            companyData.Creativity = companyData.medal.Creativity
            companyData.CreativityTH =companyData.medals?companyData.medals.Creativity? companyData.medals.Creativity.th==companyData.medals.Creativity.en?"":companyData.medals.Creativity.th:"":""
          }

          if(companyData.medal.Initiative){
            companyData.Initiative = companyData.medal.Initiative
            companyData.InitiativeTH = companyData.medals?companyData.medals.Initiative? companyData.medals.Initiative.th==companyData.medals.Initiative.en?"":companyData.medals.Initiative.th:"":""
          }

          if(companyData.medal.Kindness){
            companyData.Kindness = companyData.medal.Kindness
            companyData.KindnessTH = companyData.medals?companyData.medals.Kindness? companyData.medals.Kindness.th==companyData.medals.Kindness.en?"":companyData.medals.Kindness.th:"":""
          }

          if(companyData.medal.Strength){
            companyData.Strength = companyData.medal.Strength
            companyData.StrengthTH = companyData.medals?companyData.medals.Strength? companyData.medals.Strength.th==companyData.medals.Strength.en?"":companyData.medals.Strength.th:"":""
          }

          if(companyData.medal.Charisma){
            companyData.Charisma = companyData.medal.Charisma
            companyData.CharismaTH =companyData.medals?companyData.medals.Charisma? companyData.medals.Charisma.th==companyData.medals.Charisma.en?"":companyData.medals.Charisma.th:"":""
          }

          if(companyData.medal.Intelligence){
            companyData.Intelligence = companyData.medal.Intelligence
            companyData.IntelligenceTH =companyData.medals?companyData.medals.Intelligence? companyData.medals.Intelligence.th==companyData.medals.Intelligence.en?"":companyData.medals.Intelligence.th:"":""
          }

        }else{
          companyData.Creativity = "Creativity"
          companyData.Initiative = "Initiative"
          companyData.Kindness = "Kindness"
          companyData.Strength = "Strength"
          companyData.Charisma = "Charisma"
          companyData.Intelligence = "Intelligence"
          
        }
        if(!companyData.medals){
          companyData.CreativityTH = ""
          companyData.InitiativeTH = ""
          companyData.KindnessTH = ""
          companyData.StrengthTH = ""
          companyData.CharismaTH = ""
          companyData.IntelligenceTH = ""

        }
        if(companyData['holiday_tag'] === undefined){
          companyData['holiday_tag'] = new Array()
        }
        if(companyData['holiday_val'] === undefined){
          companyData['holiday_val'] = new Array()
        }
        await firebase.firestore().collection('holliday').where("companyid", "==", user.company).where("date",">=",format(companyNow, 'YYYY-MM-DD')).orderBy('date').get()
        .then(function (querySnapshot) {
            querySnapshot.forEach(function (doc) {
                const data = doc.data();
                let date_holiday = data['date'].split("-");

                companyData['holiday_tag'].push(format(data['date'], 'dddd, MMMM D, YYYY'))
                companyData['holiday_val'].push(new Date(`${date_holiday[0]}/${date_holiday[1]}/${date_holiday[2]}`))
            });
        });
        for(var value in companyData.hashtag){

          recognitionValue.push({
            ...companyData.hashtag[value],
            particular: { ...(companyData.hashtag[value].particular || {}) },
            value: value,
            label:companyData.hashtag[value].name,
            labelTh:companyData.hashtag[value].name_th==companyData.hashtag[value].name?"#":companyData.hashtag[value].name_th,
          });
          oldRecognitionValue.push({
            ...companyData.hashtag[value],
            particular: { ...(companyData.hashtag[value].particular || {}) },
            value: value,
            label:companyData.hashtag[value].name,
            labelTh:companyData.hashtag[value].name_th==companyData.hashtag[value].name?"#":companyData.hashtag[value].name_th,
          });
        }

          companyData.report_monthly_insights= companyData.report_monthlyboss?companyData.report_monthlyboss:"N"
          companyData.report_monthlyboss_time= companyData.report_monthlyboss_time
          companyData.happilyAssistAccess_checked= companyData.assist_access?companyData.assist_access:false
          companyData.medalsForLeaders_checked= companyData.leadership=="Y"?true:false
          companyData.recognitionValue_checked= normalizeHashtagStatus(companyData.hashtag_status)
          companyData.peerFeedbackInteractions_checked= companyData.peer_feedback=="Y"?true:false
          companyData.happilyVillageAccess_checked = companyData.house_village=="Y"?true:false
          companyData.create_topics= companyData.create_topics=="Y"?true:false
        
          applyCompanyData(companyData);
          companyData.townHallAgent_checked = Boolean(companyData.townhall_agent);
          companyData.townhall_themes = companyData.townhall_themes;
          companyData.emailNotifications = companyData.email_notifications;
          companyData.report_monthlyhr = companyData.report_monthlyhr == 'Y' ? true : false;
          companyData.report_activity_manager = companyData.report_activity_manager == 'Y' ? true : false;
          commit('setSettingOldDataMock',companyData) 
 
      },
      
      async saveChanges({commit,rootState,dispatch,state}:any,value:any){
        const companyId = rootState.authen.user.company
        const formExcels = Array.isArray(value.excels) ? value.excels : []
        const oldExcels = Array.isArray(state.oldExcels) ? state.oldExcels : []
        const actorEmail = (rootState.authen.user && rootState.authen.user.email) || ''
        const nowStamp = format(new Date(), 'YYYY-MM-DD HH:mm:ss')

        // Guard: an empty skills payload usually means the UI never loaded
        // excels (or state was wiped). Writing that would mark every active
        // skill as status=N. Only allow an empty write when the UI previously
        // had skills loaded and the user cleared them intentionally.
        const excelsUpdate = Promise.all([
          firebase.database().ref(`excels_company/${companyId}`).orderByChild('status').equalTo('Y').once('value'),
          firebase.database().ref(`excels_company/${companyId}`).once('value'),
        ]).then(([activeSnap, allSnap]) => {
          if (shouldSkipExcelsCompanyWrite({
            formExcelsCount: formExcels.length,
            oldExcelsCount: oldExcels.length,
          })) {
            console.warn('[settings] Skipping excels_company write: empty UI skills while none were loaded')
            return null
          }

          const languageTemplate = buildLanguageTemplate(state.language)
          const oldKeys = new Set(
            oldExcels
              .map((item: any) => (item && item.key != null ? String(item.key) : ''))
              .filter(Boolean)
          )
          // Preserve already-disabled skills so a save cannot delete them from RTDB.
          const set: any = {}
          allSnap.forEach((childSnapshot: any) => {
            const existing = childSnapshot.val() || {}
            set[childSnapshot.key] = { ...existing, key: childSnapshot.key }
          })

          const excel: any = []
          activeSnap.forEach((childSnapshot: any) => {
            let data = childSnapshot.val()
            data.key = childSnapshot.key
            const key = String(childSnapshot.key)
            const diff = _.find(formExcels, (item: any) => item && String(item.key) === key)
            const action = resolveActiveExcelSaveAction({
              inForm: !!diff,
              inOldExcels: oldKeys.has(key),
            })
            if (action === 'keep-form') {
              data = diff
            } else if (action === 'disable') {
              data.status = 'N'
              data.updateBy = actorEmail
              data.updateStatus = nowStamp
            }
            excel.push(data)
          })

          for (const x in formExcels) {
            const formKey = formExcels[x] && formExcels[x].key != null ? String(formExcels[x].key) : ''
            const diff = _.find(excel, (item: any) => item && String(item.key) === formKey)
            if (diff === undefined) {
              excel.push(formExcels[x])
            }
          }

          const disabledKeys: string[] = []
          for (const x in excel) {
            const key = excel[x].key
            if (!key) continue
            excel[x].names = normalizeExcelNames(excel[x], languageTemplate)
            excel[x].name_th = excel[x].names.th ? excel[x].names.th : excel[x].name
            for (const lang in excel[x].names) {
              if (lang !== 'th' && lang !== 'en') {
                if (excel[x].names[lang] == undefined) {
                  delete excel[x].names[lang]
                }
              }
            }
            if (excel[x].status === 'N' && set[key] && set[key].status === 'Y') {
              disabledKeys.push(key)
            }
            set[key] = excel[x]
          }

          if (Object.keys(set).length === 0) {
            return null
          }

          return firebase.database().ref(`excels_company/${companyId}`).set(set).then(() => {
            if (disabledKeys.length > 0) {
              dispatch('authen/addLogs', {
                company: companyId,
                createdate: nowStamp,
                table: 'excels_company',
                status: 'disable',
                id: disabledKeys.join(','),
                user: actorEmail,
                count: disabledKeys.length,
              }, { root: true })
            }
            dispatch("getExcelsByCompany");
          });
        });
        const company = value.company
        const form = value.form
        const dateNow = new Date()
        const companyTimezone = await getCompanyTimezone(company)
        const companyNow = toCompanyDate(new Date(), companyTimezone)
        //console.log("form",form)
        const countMembers = value.countMembers.replace(/\(/g, "").replace(/\)/g, "")
        let convert_1 = new Convert(form.starttime);
        let starttimeShow = convert_1.convertTime();
        let starttimeSave = starttimeShow.split(":");
        let starttimeTimezone = starttimeShow.split(":");
        let calTime;
        // if(countMembers > 500){
        //   calTime = countMembers / 500;
        // }else{
          calTime = 0
        // }
        
        starttimeSave = format(addMinutes(new Date(0,0,0,starttimeSave[0],starttimeSave[1]),calTime * -1),'HH:mm')
        let report_dailyboss_time = '00:00';
        if(form.report_dailyboss_time&&form.report_dailyboss_time != "Invalid Date"){
          let convert_2 = new Convert(form.report_dailyboss_time);
          report_dailyboss_time = convert_2.convertTime();
        }
        let report_weeklyboss_time = '00:00';
        if(form.report_weeklyboss_time&&form.report_weeklyboss_time != "Invalid Date"){
          let convert_3 = new Convert(form.report_weeklyboss_time);
          report_weeklyboss_time = convert_3.convertTime();
        }
        let report_monthlyboss_time = '00:00';
        if(form.report_monthlyboss_time&&form.report_monthlyboss_time != "Invalid Date"){
          let convert_3 = new Convert(form.report_monthlyboss_time);
          report_monthlyboss_time = convert_3.convertTime();
        }
        let reminder_time = '00:00';
        if(form.reminder_time&&form.reminder_time != "Invalid Date"){
          let convert_4 = new Convert(form.reminder_time);
          reminder_time = convert_4.convertTime(); 
        }

        if(form.timezone){
          const dateNow = new Date()
          const setlengthTimezone = [];
          setlengthTimezone['7'] = [4, 4.5, 5, 5.5, 5.75, 6, 6.5, 7, 8, 9, 9.5, 10];
          setlengthTimezone['-7'] = [-4, -5, -6, -7, -8, -9, -10];
          setlengthTimezone['0'] = [-3.5, -3, -2, -1, 0, 1, 2, 3, 3.5];
          let globalTime = 0
          if (setlengthTimezone['7'].indexOf( parseFloat(form.timezone)) !== -1)
              globalTime = 7;
          if (setlengthTimezone['-7'].indexOf( parseFloat(form.timezone)) !== -1)
              globalTime = -7;
          if (setlengthTimezone['0'].indexOf( parseFloat(form.timezone)) !== -1)
              globalTime = 0;
              
          const diffTimezone = form.timezone - globalTime
          // const arr_starttimeShow:any = starttimeShow.split(":")
          // starttimeShow = format(subHours(dateNow.setHours(arr_starttimeShow[0], arr_starttimeShow[1]),diffTimezone), 'HH:mm')

          starttimeTimezone = format(dateNow.setHours(starttimeTimezone[0], starttimeTimezone[1]), 'HH:mm')

          const arr_starttimeSave:any = starttimeSave.split(":")
          starttimeSave = format(subHours(dateNow.setHours(arr_starttimeSave[0], arr_starttimeSave[1]),diffTimezone), 'HH:mm')

          const arr_report_dailyboss_time:any = report_dailyboss_time.split(":")
          report_dailyboss_time = format(subHours(dateNow.setHours(arr_report_dailyboss_time[0], arr_report_dailyboss_time[1]),diffTimezone), 'HH:mm')

          const arr_report_weeklyboss_time:any = report_weeklyboss_time.split(":")
          report_weeklyboss_time = format(subHours(dateNow.setHours(arr_report_weeklyboss_time[0], arr_report_weeklyboss_time[1]),diffTimezone), 'HH:mm')
          
          const arr_report_monthlyboss_time:any = report_monthlyboss_time.split(":")
          report_monthlyboss_time = format(subHours(dateNow.setHours(arr_report_monthlyboss_time[0], arr_report_monthlyboss_time[1]),diffTimezone), 'HH:mm')

          
          const arr_reminder_time:any = reminder_time.split(":")
          reminder_time = format(subHours(dateNow.setHours(arr_reminder_time[0], arr_reminder_time[1]),diffTimezone), 'HH:mm')
        }
        const performance_review=_.cloneDeep(form.performance_review)
        const reset_completed_monthly=form.performance_review&&form.performance_review.reset_completed_monthly?form.performance_review.reset_completed_monthly:false
        delete performance_review.reset_completed_monthly
        // Drop the cached getCompany() payload before writing so the post-save
        // refresh in settings.vue forces a real network fetch instead of
        // returning the pre-save snapshot and reverting the toggle.
        invalidateCompanyCache(company)
        const companyUpdate = firebase.database().ref(`/company/${company}`).update({
          "medal":{
            Creativity: form.Creativity,
            Initiative: form.Initiative,
            Kindness: form.Kindness,
            Strength: form.Strength,
            Charisma: form.Charisma,
            Intelligence: form.Intelligence,
          },
          "medals":{
            Creativity:{en: form.Creativity,th: form.CreativityTH},
            Initiative:{en: form.Initiative,th: form.InitiativeTH}, 
            Kindness: {en: form.Kindness,th: form.KindnessTH}, 
            Strength:{en: form.Strength,th: form.StrengthTH}, 
            Charisma:{en: form.Charisma,th: form.CharismaTH}, 
            Intelligence: {en: form.Intelligence,th: form.IntelligenceTH},
          },
          house_village: form.happilyVillageAccess_checked?"Y":"N",
          battle_stadium_scoring: {
            ...(form.battle_stadium_scoring || {}),
            fair_play_boost_enabled: Boolean(
              form.battle_stadium_scoring &&
              form.battle_stadium_scoring.fair_play_boost_enabled === true
            ),
          },
          assist_access:form.happilyAssistAccess_checked,
          leadership: form.medalsForLeaders_checked?"Y":"N",
          starttime: starttimeSave,
          starttime_timezone: starttimeTimezone,
          hashtag_status:normalizeHashtagStatus(form.recognitionValue_checked),
          peer_feedback:form.peerFeedbackInteractions_checked?"Y":"N",
          performance_review: performance_review || {enabled: true, overdue: 2, pending: 1, ai_review_feedback: true},
          performance_conversations: form.performance_conversations || { enabled: false, min_days_between: 7, ai_timeline_summary: true },
          limit: form.limit || {openend: 3},
          // starttime_show: starttimeShow,
          reminder_time: reminder_time,
          report_dailyboss_time: report_dailyboss_time,
          report_weeklyboss_time: report_weeklyboss_time,
          report_monthlyboss_time:report_monthlyboss_time,
          reset_completed_monthly:reset_completed_monthly,
          create_topics: form.create_topics==true?"Y":"N",

          // Town Hall Agent
          townhall_agent: form.townHallAgent_checked,
          townhall_themes: state.townHallThemes,

          // Email Notifications - BACKEND TODO: Implement email sending logic
          //
          // When these settings are saved, the backend should trigger email notifications based on these rules:
          //
          // 1. managerRewardRedemption: true
          //    - TRIGGER: When employee redeems a reward
          //    - RECIPIENT: Direct manager of the employee
          //    - EMAIL CONTENT: Employee name, reward details, redemption date
          //    - IMPLEMENTATION: Add notification logic to reward redemption handler
          //
          // 2. managerPeerFeedbackOutstanding: true
          //    - TRIGGER: When employee receives "Truly Outstanding" peer feedback rating
          //    - RECIPIENT: Direct manager of the employee
          //    - EMAIL CONTENT: Employee name, feedback details, rating info
          //    - IMPLEMENTATION: Add notification logic to peer feedback submission handler
          //
          // 3. managerActivityReport: true
          //    - TRIGGER: Scheduled (weekly/monthly - TBD)
          //    - RECIPIENT: All managers in company
          //    - EMAIL CONTENT: Manager's activity summary, team metrics, subordinate manager data if applicable
          //    - IMPLEMENTATION: Create scheduled job/cron that generates and sends reports
          //
          // 4. hrMonthlyReports: true
          //    - TRIGGER: Monthly schedule
          //    - RECIPIENT: All users with role "hr"
          //    - EMAIL CONTENT: Company-wide metrics, engagement data, insights
          //    - IMPLEMENTATION: Create monthly scheduled job for HR reporting
          //
          // HELPER FUNCTIONS NEEDED:
          // - getCompanyEmailSettings(companyId) - reads this data from Firebase
          // - getDirectManager(employeeId) - finds employee's manager
          // - getManagers(companyId) - gets all managers in company
          // - getHRUsers(companyId) - gets all HR users in company
          // - Email sending functions for each notification type
          //
          // FIREBASE PATH: /company/{companyId}/email_notifications
          // DATA STRUCTURE: { managerRewardRedemption: boolean, managerPeerFeedbackOutstanding: boolean, managerActivityReport: boolean, hrMonthlyReports: boolean }
          email_notifications: form.emailNotifications,
          activity_feed_use_nickname: form.activity_feed_use_nickname,
          activity_feed_show_rewards: form.activity_feed_show_rewards,
          gift_cross_location: form.giftCrossLocationRewards_checked ? 'Y' : 'N',
          report_monthlyhr: form.report_monthlyhr ? 'Y' : 'N',
          report_activity_manager: form.report_activity_manager ? 'Y' : 'N',
          feedback_rollup_scope: FEEDBACK_ROLLUP_SCOPES.includes(form.feedback_rollup_scope) ? form.feedback_rollup_scope : 'default',
          updated:format(new Date(), 'YYYY-MM-DD HH:mm')
        })
        // Invalidate again once the write actually lands, in case a concurrent
        // read between the sync invalidation above and this resolution
        // repopulated the cache with the pre-save payload.
        const companyCacheUpdate = companyUpdate.then(() => invalidateCompanyCache(company))

        let data={}
       
        for(var i in value.recognitionValue){
          const recognitionNames = {
            ...(value.recognitionValue[i].names || {}),
            en: value.recognitionValue[i].label,
          }
          const rawThai = hasOwn(value.recognitionValue[i].names, 'th')
            ? value.recognitionValue[i].names.th
            : value.recognitionValue[i].name_th !== undefined
              ? value.recognitionValue[i].name_th
              : value.recognitionValue[i].labelTh
          recognitionNames.th = !rawThai || rawThai == "#" ? value.recognitionValue[i].label : rawThai

          data[value.recognitionValue[i].value]={
            name:value.recognitionValue[i].label,
            name_th:recognitionNames.th,
            order:parseInt(i)+1,
            names: recognitionNames,
          }
          for(var j in value.recognitionValue[i]){
            if(j!=='name'&&j!=='name_th'&&j!=='order'&&j!=='label'&&j!=='labelTh'&&j!=='value'){
              data[value.recognitionValue[i].value][j]=value.recognitionValue[i][j]
            }
          }
          data[value.recognitionValue[i].value].names = recognitionNames
         
        }
       
        const hashtagUpdate = firebase.database().ref(`/company/${rootState.authen.user.company}/hashtag`).set(data)
        let holliday_query = firebase.firestore().collection('holliday').where("companyid", "==", company).where("date",">=",format(companyNow, 'YYYY-MM-DD')).orderBy('date');
        const holidayUpdate = holliday_query.get().then(function(querySnapshot) {
          const holidayWrites: Array<Promise<any>> = []
          querySnapshot.forEach(function(doc) {
            holidayWrites.push(doc.ref.delete());
          });
            if(form.holiday_val && form.holiday_val.length > 0){
              _.forEach(form.holiday_val, (value, index) => {
                let dateHoliday = format(value, 'YYYY-MM-DD');
                let dateHoliday_split = dateHoliday.split("-");;
                holidayWrites.push(firebase.firestore().collection("holliday").doc(company +'-'+ dateHoliday).set({
                  company:{
                    [company]: "Y",
                  },
                  companyid: company,
                  d: dateHoliday_split[2],
                  m: dateHoliday_split[1],
                  y: dateHoliday_split[0],
                  date: dateHoliday,
                  update: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                }))
              });
            }

            return Promise.all(holidayWrites);
        });

        if (form.happilyVillageAccess_checked ) {
          var key: any = '';
          var xp_running = '';
          var distance_want = '';
          var xp_want = '';
          var newId = company;
          firebase.firestore().collection('houses').where('company','==', newId).get().then((querySnapshot)=> {
            firebase.database().ref('houses_range').orderByChild("min").equalTo(0).once('value', (snapshot) => {
                  snapshot.forEach((childSnapshot) => {
                      key = childSnapshot.key
                  });
                  firebase.database().ref('houses_achievement/'+key+'-mr').once('value', (snapshot) => {
                      var achievement = snapshot.val()
                      if(achievement != null){
                          xp_running = achievement.xp
                      }
                      let today = new Date();
                      let mm = today.getMonth() + 1;
                      firebase.database().ref('houses_monthlyrun/'+key+'-'+mm).once('value', (snapshot) => {
                          var monthlyrun = snapshot.val()
                          if(monthlyrun != null){
                              distance_want = monthlyrun.xp
                          }
                          firebase.database().ref('houses_xp/'+key+'-1').once('value', (snapshot) => {
                              var houses_xp = snapshot.val()
                              if(houses_xp != null){
                                  xp_want = houses_xp.xp
                              }
                              firebase.database().ref('houses').once('value', (snapshot) => {
                                  snapshot.forEach((childSnapshot) => {
                                      var _data = childSnapshot.val();
                                      _data.key = childSnapshot.key;
                                      var newrowid = newId+_data.key
                                      var house_data = {
                                          company : newId,
                                          house   : _data.key,
                                          house_name : _data.name,
                                          color : _data.color,
                                          level : 1,
                                          members : 0,
                                          xp : 0,
                                          // xp_running : xp_running,
                                          distance_want : distance_want,
                                          rang_id : key,
                                          xp_want : xp_want,
                                          xp_running_want : xp_running,


                                      }
                                      // New houses in Firestore , first open House Village
                                      // console.log(newrowid)
                                      // console.log(house_data)
                                      if(querySnapshot.empty){
                                          firebase.firestore().collection('houses').doc(newrowid).set(house_data).then(ref => {})
                                      }

                                      if(_data.image)
                                          _data.image = _data.image
                                      else
                                          _data.image = ''
                                      
                                      // New running_track_month_house in Firestore , first open House Village
                                      var running_track_month_house = {
                                          color : _data.color,
                                          company : newId,
                                          // date : moment.tz('Asia/Bangkok').format('YYYY-MM'),
                                          date : format(new Date(), 'YYYY-MM'),
                                          distance : 0,
                                          distance_want : distance_want,
                                          house   : _data.key,
                                          house_name : _data.name,
                                          xp_running_want : xp_running,
                                      }
                                      var id_running_track = newId+_data.key+'-'+format(new Date(), 'YYYY-MM')
                                      // var deleteDoc =firestore.collection('running_track_month_house/').doc(id_running_track).delete();
                                      // console.log(id_running_track)
                                      // console.log(running_track_month_house)
                                      firebase.firestore().collection('running_track_month_house/').doc(id_running_track).get().then((doc)=> {
                                          if (!doc.exists) {
                                            firebase.firestore().collection('running_track_month_house').doc(id_running_track).set(running_track_month_house).then(ref => {})
                                          }else{
                                           
                                          }
                                      })
                                    });
                                    value.messageLoading.close();
                                    value.thisvue.$message({
                                      type: 'success',
                                      message: 'Your settings have been updated',
                                    });
                              });
                          });
                      });
                  });
              });
          });
        }else{
          value.messageLoading.close();
          value.thisvue.$message({
            type: 'success',
            message: 'Your settings have been updated',
          });
        }

        return Promise.all([excelsUpdate, companyCacheUpdate, hashtagUpdate, holidayUpdate]).then(() => undefined)
        
      },
      async getExcelsByCompany({commit,rootState,state}:any){
        if (pendingExcelsFetch) return pendingExcelsFetch

        const company = rootState.authen && rootState.authen.user && rootState.authen.user.company
        if (!company) return

        const run = (async () => {
          const [excels_master, excels_detail, companySnap] = await Promise.all([
            firebase.database().ref(`excels`).once('value').then(s => s.val()),
            getExcelsDetail(),
            firebase.database()
              .ref(`excels_company/${company}`)
              .orderByChild('status').equalTo('Y')
              .once('value'),
          ])

          commit('setExcelsDetail', excels_detail)

          const language = buildLanguageTemplate(state.language)
          const excel: any[] = []
          companySnap.forEach((childSnapshot) => {
            const data = childSnapshot.val()
            data.key = childSnapshot.key
            const _data = _.clone(data)
            _data.names = normalizeExcelNames(_data, language)
            _data.name_th = data.name_th == data.name ? '' : data.name_th
            _data.status_master = !!(excels_master && excels_master[data.key])
            excel.push(_data)
          })

          commit('setExcels', excel)
          commit('setOldExcels', _.cloneDeep(excel))
        })()

        pendingExcelsFetch = run.finally(() => {
          pendingExcelsFetch = null
        })
        return pendingExcelsFetch
      },
      getoldExcelsByCompany({commit,rootState}:any){
        firebase.database().ref(`excels_company/${rootState.authen.user.company}`).orderByChild('status').equalTo('Y').once('value', (snapshot) => {
          const excel: string[] = []
          snapshot.forEach((childSnapshot) => {
            const data = childSnapshot.val()
            data.key = childSnapshot.key
            data.name_th=data.name_th==data.name?"":data.name_th
            excel.push(data)
          });
          commit('setOldExcels', excel)
        });
      },
      removeExcels({commit,rootState,state,dispatch}:any,item){
        if(item.key){
          /*firebase.database().ref(`excels_company/${rootState.authen.user.company}/${item.key}`).update({
            status:'N'
          });*/
          const data = _.clone(state.excels)
          _.remove(data, (e:any) => {
            return e.key == item.key;
          });
          commit('setExcels',data)
          
        }
      },
      createExcels({commit,rootState,state,dispatch}:any,item){
        if(item){
          /*firebase.database().ref(`excels_company/${rootState.authen.user.company}`).push({
            name:item,
            name_th:item,
            status:'Y',
            updated: format(new Date(), 'YYYY-MM-DD HH:mm')
          }).then((snap)=>{
            
          });*/
          const data = _.clone(state.excels)
          data.push({
            name:item,
            name_th:item,
            status:'Y',
            updated: format(new Date(), 'YYYY-MM-DD HH:mm'),
            createdate: format(new Date(), 'YYYY-MM-DD HH:mm'),
            key:`rand-${Math.floor(Math.random() * 999999)}`,
          })
          commit('setExcels',data)
         
          
        }
      },
      // saveRecognition({commit,rootState,dispatch,state}:any,value:any){
      //   let data={}
      //   for(var i in value.recognitionValue){
      //     data[value.recognitionValue[i].value]={
      //       name:value.recognitionValue[i].label,
      //       name_th:value.recognitionValue[i].label
      //     }
      //   }
      //   let status={
      //     hashtag_status:value.settingDataMock.recognitionValue_checked
      //   }

      // //  firebase.database().ref(`/company/${rootState.authen.user.company}`).update(status)
      //  firebase.database().ref(`/company/${rootState.authen.user.company}/hashtag`).set(data)
      // //  commit("setSettingOldDataMock",value.settingDataMock)
      // //  commit("setSettingOldDataMock",value.recognitionValue)
       
      // },
      newIdhashtag({commit,rootState,dispatch,state}:any,value:any){
        let newid=firebase.database().ref(`/company/${rootState.authen.user.company}/hashtag`).push().key;
        commit("sethashTagId",newid)
      },
      async getmemberInteam({commit,state,dispatch,rootState}:any, value:any){
        //console.log('getmemberInteam',value)
        commit('setMultipleState',{loading_preset:true})
        //commit('setloading_preset',true)
        const organization1level:any={}
        let user_all=await getUserAll(rootState.authen.user.company)//(await firebase.database().ref('users').orderByChild("company").equalTo(rootState.authen.user.company).once('value')).val()
        const group_memberinteam:any=_.groupBy(user_all, function (b) {
          return b.team;
        });
        
        const team_all=await getTeamByCompany(rootState.authen.user.company)//(await firebase.database().ref('team').orderByChild("company").equalTo(rootState.authen.user.company).once('value')).val()
        const cohorts_all=(await firebase.database().ref(`cohorts/${rootState.authen.user.company}`).once('value')).val() || {}
        const cohortOptions=buildCohortOptions(cohorts_all)
        const countryOptions=buildCountryOptions(user_all)
        const memberinteam_all:any={}
        let memberinteam:any={}
        await firebase.firestore().collection('organization1level').where("company", "==", rootState.authen.user.company).get()
            .then((querySnapshot)=> {
                    querySnapshot.forEach((doc)=> {
                        const data=doc.data()
                        if(!organization1level[data.team_id]){
                          organization1level[data.team_id]=data.parent
                        }
                        if(!memberinteam_all[data.team_id]){
                          memberinteam_all[data.team_id]={users:[],name:team_all[data.team_id]?team_all[data.team_id].name:'',id:data.team_id}
                        }
                        if(!memberinteam[data.team_id]){
                          memberinteam[data.team_id]={users:[],name:team_all[data.team_id]?team_all[data.team_id].name:'',id:data.team_id}
                        }
                        if(data.parent){
                          for(const key  in data.parent){
                           
                       
                            if(group_memberinteam[key]){
                              memberinteam_all[data.team_id].users.push(...group_memberinteam[key])
                              memberinteam[data.team_id].users.push(...group_memberinteam[key])
                            }
                          }
                          
                        }   
                                      
                    });
                    for(const key in team_all){
                      if(!memberinteam_all[key]){
                        if(group_memberinteam[key]){
                          memberinteam_all[key]={users:group_memberinteam[key],name:team_all[key].name,id:key}
                        }
                      }
                      if(!memberinteam[key]){
                        if(group_memberinteam[key]){  
                          memberinteam[key]={users:group_memberinteam[key],name:team_all[key].name,id:key}
                        }
                      }
                    }
                    //console.log("memberinteam",memberinteam)
                    memberinteam=_.orderBy(memberinteam, 'name', 'asc')
                    for(const key in user_all){
                      const teamId = user_all[key] && user_all[key].team
                      const teamInfo = teamId && team_all ? team_all[teamId] : null
                      user_all[key].team_name = teamInfo && teamInfo.name ? teamInfo.name : ''
                    }
                    user_all=_.orderBy(user_all, 'firstname', 'asc')
                  
                    const payload={
                      organization1level: organization1level,
                      memberinteam: memberinteam,
                      memberInteamAll_master: memberinteam_all,
                      memberInteamAll: memberinteam_all,
                      cohortOptions: cohortOptions,
                      cohortOptionsAll: cohortOptions,
                      countryOptions: countryOptions,
                      countryOptionsAll: countryOptions,
                      allUser: user_all,
                      member: user_all
                    }
                    commit('setMultipleState', payload)
                    dispatch('getPresets',value)
                   
                    
                    
             });
        
      },
      async searchmemberandteamandMember({commit,state,dispatch,rootState}:any, value:any){
        //console.log('searchmemberandteam',value)
        const type=value.type
        let memberinteam:any={}
        var re = new RegExp(value.value, 'gi')
        if(type=='team'){
          
          const _memberinteam:any=_.cloneDeep(state.memberInteamAll)
          if (value.value) {
            for(const key in _memberinteam){
              if(_memberinteam[key].name.match(re)){
                memberinteam[key]=_memberinteam[key]
              }
            }
            //console.log("memberinteam",memberinteam)
           
            memberinteam=_.orderBy(memberinteam, 'name', 'asc')
            commit('setMultipleState', {memberinteam: memberinteam})
          }else{
            commit('setMultipleState', {memberinteam:state.memberInteamAll})
          }
        }
        else if(type=='cohort'){
          const cohorts:any=_.cloneDeep(state.cohortOptionsAll)
          if (value.value) {
            const filtered:any=[]
            for(const key in cohorts){
              if(cohorts[key].name.match(re)){
                filtered.push(cohorts[key])
              }
            }
            commit('setMultipleState', {cohortOptions:_.orderBy(filtered, 'name', 'asc')})
          }else{
            commit('setMultipleState', {cohortOptions:state.cohortOptionsAll})
          }
        }else if(type=='country'){
          const countries:any=_.cloneDeep(state.countryOptionsAll)
          if (value.value) {
            const filtered:any=[]
            for(const key in countries){
              if(countries[key].name.match(re) || countries[key].code.match(re)){
                filtered.push(countries[key])
              }
            }
            commit('setMultipleState', {countryOptions:_.orderBy(filtered, 'name', 'asc')})
          }else{
            commit('setMultipleState', {countryOptions:state.countryOptionsAll})
          }
        }else{
          const _memberinteam:any=_.cloneDeep(state.allUser)
          if (value.value) {
            for(const key in _memberinteam){
              //console.log("key",_memberinteam[key].firstname)
              if(_memberinteam[key].firstname.match(re)){
                memberinteam[key]=_memberinteam[key]
              }else if(_memberinteam[key].lastname.match(re)){
                memberinteam[key]=_memberinteam[key]
              }else if(_memberinteam[key].email.match(re)){
                memberinteam[key]=_memberinteam[key]
              }else if(_memberinteam[key].team_name.match(re)){
                memberinteam[key]=_memberinteam[key]
              }
            }
            //console.log("memberinteam",memberinteam)
            memberinteam=_.orderBy(memberinteam, 'firstname', 'asc')
            commit('setMultipleState', {member:memberinteam})
           
          }else{
            commit('setMultipleState', {member:state.allUser})
          }
        }
      },
      async getPresets({commit,state,dispatch,rootState}:any){
        //console.log("getPresets")
        commit('setMultipleState',{loading_preset:true})
        // commit('setloading_preset',true)
        let memberInteamAll=_.cloneDeep(state.memberInteamAll_master)
        //console.log("memberInteamAll",memberInteamAll)
        const preset_list:Array<any>=[]
        const team_all=await getTeamByCompany(rootState.authen.user.company)//(await firebase.database().ref('team').orderByChild("company").equalTo(rootState.authen.user.company).once('value')).val()
        const user_all=await getUserAll(rootState.authen.user.company)//(await firebase.database().ref('users').orderByChild("company").equalTo(rootState.authen.user.company).once('value')).val()
        const boss_all=(await firebase.database().ref(`boss/${rootState.authen.user.company}`).once('value')).val()
        const cohorts_all=(await firebase.database().ref(`cohorts/${rootState.authen.user.company}`).once('value')).val() || {}
        const cohortOptions=buildCohortOptions(cohorts_all)
        const countryOptions=buildCountryOptions(user_all)
        const team_used_preset:Array<any>=[]
        const user_used_preset:Array<any>=[]
        firebase.firestore().collection('holiday_template').where("company", "==", rootState.authen.user.company).get()
        .then(async function (querySnapshot) {
          querySnapshot.forEach((doc) => {
            const data = doc.data()
            data.id=doc.id
            data.focus_preset=false
            //console.log("data",data.teams)
            if(data.teams){
              if(!team_used_preset[data.id]){
                team_used_preset[data.id]=[]
              }
              team_used_preset[data.id].push(...data.teams)
            }
            if(data.users){
              if(!user_used_preset[data.id]){
                user_used_preset[data.id]=[]
              }
              user_used_preset[data.id].push(...data.users)
            }
            preset_list.push(data)
          })
          const member_groupbyteam:any=_.groupBy(user_all, function (b) {
            return b.team;
          });
          
          
          for(const item of preset_list){
            
            item.total_assignments=0
            item.data_users=[]
            item.data_team=[]
            item.data_cohorts=[]
            item.data_countries=[]
            if(item.users){
              for(const x of item.users){
                const emailKey=x.replace(/\./g, "||")
                const user:any={}
                if(user_all[emailKey]){
                  user.email=user_all[emailKey].email
                  user.firstname=user_all[emailKey].firstname
                  user.lastname=user_all[emailKey].lastname
                  user.boss=user_all[emailKey].boss
                  user.team=user_all[emailKey].team
                  user.team_name=team_all[user_all[emailKey].team]?team_all[user_all[emailKey].team].name:''
                  user.profilepic_crop=user_all[emailKey].profilepic_crop
                  item.data_users.push(user)
                }
                item.total_assignments+=1
                for(const key in memberInteamAll){
                  const _user=_.filter(memberInteamAll[key].users, function(o) { return o.email!==user.email });
                  if(_user&&_user.length>0){
                    //console.log("memberInteamAll",_user)
                    // const _member=_.find(memberInteamAll[key].users, function(o) { return o.email==user.email });
                    _user.preset_name=item.name
                    memberInteamAll[key].users=_user
                  }
                }


              }

            }
            
            if(item.teams){
              for(const team of item.teams){
                
                const organization1level=state.organization1level[team]
                const team_data=team_all[team]
                let memberbyteem=0
                if(organization1level){
                  for(const team_id in organization1level){

                    if(member_groupbyteam[team_id]){
                      for(const user of member_groupbyteam[team_id]){
                        
                        //check user used preset
                        for(const id_preset in user_used_preset){
                          const _finduser=_.find(user_used_preset[id_preset], function(o) { return o==user.email });
                          if(_finduser){
                              user.user_preset_id=id_preset
                              
                          }
                        }
                        for(const id_preset in team_used_preset){
                          const _findteam=_.find(team_used_preset[id_preset], function(o) { return o==user.team });
                            if(_findteam){
                              if(!user.user_preset_id){
                                user.team_preset_id=id_preset
                              }
                              // user team
                              for(const key in memberInteamAll){
                                const _user=_.filter(memberInteamAll[key].users, function(o) { return o.email==user.email });
                                if(_user&&_user.length>0&&key!==user.team){
                                  const _user_team=_.filter(memberInteamAll[key].users, function(o) { return o.email!==user.email });
                                  memberInteamAll[key].users=_user_team
                                  
                             
                                }
                              }

                            }



                          }
                              if(!user.team_preset_id&&!user.user_preset_id){
                             
                                //console.log("id_prese!!!!!!!!!",id_preset)
                                const emailkey_boss=user.boss.replace(/\./g, "||")
                                  
                                const temp_team=boss_all[emailkey_boss].team

                                
                                
                              
                                const checkteam_preset=await dispatch('checkTeamUsed',{user_email:user.email,boss_email:user.boss,team:temp_team,alluser:user_all,boss_all:boss_all,team_used_preset:team_used_preset})
                             

                                
                                  const _user=_.filter(memberInteamAll[team].users, function(o) { return o.email==user.email });
                                  if(_user&&_user.length>0&&team!==(checkteam_preset&&checkteam_preset.team_id)){
                                    const _user_team=_.filter(memberInteamAll[team].users, function(o) { return o.email!==user.email });
                                    memberInteamAll[team].users=_user_team                              
                                  }

                                
                                

                                


                                if(checkteam_preset&&checkteam_preset.id_preset==item.id){
                                  
                                  
                                  memberbyteem+=1
                                  item.total_assignments+=1
                                }
                              }else if(user.team_preset_id==item.id){
                                memberbyteem+=1
                                item.total_assignments+=1

                              }
                          //}

                        }
                    }




                    // }
                  }
                  if(team_data){
                    team_data.membersteams=memberbyteem
                    team_data.id=team
                    item.data_team.push(team_data)
                  }
                }else{
                  const team_data=team_all[team]
                  if(member_groupbyteam[team]){
                    if(team_data){
                      team_data.membersteams=member_groupbyteam[team].length
                      item.total_assignments+=member_groupbyteam[team].length
                      team_data.id=team
                      item.data_team.push(team_data)
                    }
                    
                  }
                  
                }
              }
            }

            if(item.cohorts){
              for(const cohort of item.cohorts){
                const cohort_data:any=_.find(cohortOptions, {id:cohort}) || {id:cohort, name:'[Archived Cohort]', membercount:0, users:[]}
                item.data_cohorts.push(cohort_data)
                item.total_assignments+=cohort_data.membercount || 0
              }
            }

            if(item.countries){
              for(const country of item.countries){
                const countryCode = normalizeCountryCode(country)
                const country_data:any=_.find(countryOptions, {code:countryCode}) || {id:countryCode, code:countryCode, name:getCountryName(countryCode), membercount:0, users:[]}
                item.data_countries.push(country_data)
                item.total_assignments+=country_data.membercount || 0
              }
            }

            
          }
         
          memberInteamAll=_.orderBy(memberInteamAll, 'name', 'asc')
          const payload={
            memberinteam: memberInteamAll,
            memberInteamAll: memberInteamAll,
            cohortOptions: cohortOptions,
            cohortOptionsAll: cohortOptions,
            countryOptions: countryOptions,
            countryOptionsAll: countryOptions,
            presetlist:preset_list,
            presetlistAll:preset_list,
          }
          commit('setMultipleState', payload)
          commit('setMultipleState',{loading_preset:false})
      
        })
        
      },
      async checkTeamUsed({state,rootState,dispatch}:any, value:any){
        
        const user_all=value.alluser
        const boss_all=value.boss_all
        const user_email=value.user_email
        const boss_email=value.boss_email
        const team_id=value.team
        const team_used_preset=value.team_used_preset
        for(const id_preset in team_used_preset){
          
          if(team_id!=''){
          const _findteam=_.find(team_used_preset[id_preset], function(o) { return o==team_id });
            if(_findteam){
    
              return {id_preset:id_preset,team_id:team_id}
            }
          }
        }
        const emailkey = boss_email.replace(/\./g, "||")
        if (user_all[emailkey] && user_all[emailkey].boss) {
        const emailkey=boss_email.replace(/\./g, "||")
        const temp_boss=user_all[emailkey].boss
        const emailkey_boss=user_all[emailkey].boss.replace(/\./g, "||")
        const temp_team=user_all[emailkey_boss].team
        if (temp_team) {
          return await dispatch('checkTeamUsed',{user_email:user_email,boss_email:temp_boss,team:temp_team,alluser:user_all,boss_all:boss_all,team_used_preset:team_used_preset})
        }
        }
      },
      async deletePreset({commit,state,dispatch,rootState}:any, value:any){
        //console.log('deletePreset',value)
        await firebase.firestore().collection('holiday_template').doc(value.preset_id).delete()
        dispatch('getPresets')
      },
      async updateAndCreatePreset({commit,state,dispatch,rootState}:any, value:any){
       // console.log('updateAndCreatePreset',value)
        commit('setMultipleState',{loading_preset:true})
        //commit('setloading_preset',true)
        const preset_form=_.cloneDeep(value.preset)
        const duplicate=_.cloneDeep(value.duplicate)
        if(duplicate){
          for(const key in duplicate){
            const duplicateUpdates:any={
              modified_at:format(new Date(),'YYYY-MM-DD HH:mm:ss')
            }
            if(duplicate[key].status_duplicate_team==true){
              duplicateUpdates.teams=duplicate[key].teams || []
            }
            if(duplicate[key].status_duplicate_user==true){
              duplicateUpdates.users=duplicate[key].users || []
            }
            if(duplicate[key].status_duplicate_cohort==true){
              duplicateUpdates.cohorts=duplicate[key].cohorts || []
            }
            if(duplicate[key].status_duplicate_country==true){
              duplicateUpdates.countries=duplicate[key].countries || []
            }
            if(Object.keys(duplicateUpdates).length > 1){
              await firebase.firestore().collection('holiday_template').doc(duplicate[key].id).update(duplicateUpdates)
            }
          }
        }
        let user:any=[]
        let team:any=[]
        let cohort:any=[]
        let country:any=[]
        if(preset_form.users){
          for(const i in preset_form.users){
            //console.log("i",i)
            user.push(preset_form.users[i].email)
          }
        }
        if(preset_form.team){
          for(const i in preset_form.team){

            team.push(preset_form.team[i].id)
          }
        }
        if(preset_form.cohorts){
          for(const i in preset_form.cohorts){
            cohort.push(preset_form.cohorts[i].id)
          }
        }
        if(preset_form.countries){
          for(const i in preset_form.countries){
            country.push(normalizeCountryCode(preset_form.countries[i].code || preset_form.countries[i].id))
          }
        }
        let holiday_day={'Monday':false,'Tuesday':false,'Wednesday':false,'Thursday':false,'Friday':false,'Saturday':false,'Sunday':false}
        if(preset_form.dayoff){
          for(const i in preset_form.dayoff){
           
            holiday_day[preset_form.dayoff[i]]=true
            
            //console.log("i",holiday_day[preset_form.dayoff[i]])
            
            
          }
        }

       // console.log("user",user)
        // Process holiday_dates - convert Date objects to YYYY-MM-DD strings
        let holiday_dates: string[] = []
        if (preset_form.holiday_dates_val && preset_form.holiday_dates_val.length > 0) {
          for (const dateVal of preset_form.holiday_dates_val) {
            if (dateVal instanceof Date) {
              holiday_dates.push(format(dateVal, 'YYYY-MM-DD'))
            } else if (typeof dateVal === 'string') {
              holiday_dates.push(dateVal)
            }
          }
        }
        
        let newId=''
        const data:any={
          name:preset_form.name,
          users:user,
          company:rootState.authen.user.company,
          holiday_day:holiday_day,
          holiday_dates:holiday_dates,  // NEW: specific dates
          company_holiday:preset_form.company_holiday,
          teams:team,
          cohorts:cohort,
          countries:country,
          modified_by:rootState.authen.user.email,
          status:true,
        }
        if(!preset_form.team){
          delete data.teams
        }
        if(!preset_form.users){
          //console.log("preset_form.users",preset_form.users)
          delete data.users
        }
        if(!preset_form.cohorts){
          delete data.cohorts
        }
        if(!preset_form.countries){
          delete data.countries
        }
       // console.log("data",data)
        try {
        if(preset_form.preset_id){

          newId=preset_form.preset_id
          data.modified_at=format(new Date(),'YYYY-MM-DD HH:mm:ss')
          await firebase.firestore().collection('holiday_template').doc(newId).update(data)
          //.log('update preset success')
        }else{

          //console.log('create preset')
          data.created_at=format(new Date(),'YYYY-MM-DD HH:mm:ss')
          await firebase.firestore().collection('holiday_template').doc().set(data) 
      
          //console.log('create preset success')
        }
        dispatch('getPresets')
        } catch (error) {
          //console.error('Error updating or creating preset:', error)
        }
       
        

      },
      async searchData_preset({commit,state,dispatch,rootState}:any, value:any){
        //.log('searchData_preset',value)
        var re = new RegExp(value, 'gi') 
        const presetlistAll:any=_.cloneDeep(state.presetlistAll)
        //console.log("presetlistAll",presetlistAll)
        const presetlist:any=[]
        if (value) {
          for(const key in presetlistAll){
            //console.log("presetlistAll[key].name",presetlistAll[key].name)
            if(presetlistAll[key].name.match(re)){
              presetlist.push(presetlistAll[key])
            }
          }
          //console.log("presetlist",presetlist)
          commit('setMultipleState',{presetlist:presetlist})
        
        }else {
          commit('setMultipleState',{presetlist:presetlistAll})
        }
       
      },
      async savePeerFeedbackTagChanges({commit,state,dispatch,rootState}:any, value:any){
        const data=_.cloneDeep(value)
         
        const excels_detail=data.attributes
        const master_excels_detail=_.cloneDeep(state.excels_detail)
        master_excels_detail[data.key]=excels_detail
        commit('setExcelsDetail',master_excels_detail)
        //console.log('master_excels_detail',master_excels_detail)
        if(data.new_excel){
          const names = {
            ...(data.names || {}),
            en: data.name,
            th: data.name_th ? data.name_th : data.name,
          }
          await firebase.database().ref(`excels_company/${rootState.authen.user.company}/${data.key}`).set({
            name:data.name,
            key:data.key,
            name_th:data.name_th?data.name_th:data.name,
            names,
            status:"Y",
            type:"value_based",
            updateStatus:format(new Date(),'YYYY-MM-DD HH:mm:ss'),
            updateBy:rootState.authen.user.email,
            createdate:format(new Date(),'YYYY-MM-DD HH:mm:ss'),
          }).then(()=>{
            dispatch('getExcelsByCompany')
            dispatch('authen/addLogs',{
              company:rootState.authen.user.company,
              createdate:format(new Date(),'YYYY-MM-DD HH:mm:ss'),
              table:'excels_company',
              status:'create',
              id:data.key,
              user:rootState.authen.user.email
            },{root:true})
          })
        }
        if(excels_detail){
          await firebase.database().ref(`excels_detail/${data.key}`).set(excels_detail).then(()=>{
            /*save logs*/
          dispatch(
            "authen/addLogs",
            {
              company: rootState.authen.user.company,
              createdate: format(new Date(), "YYYY-MM-DD HH:mm:ss"),
              table: "excels_detail",
              status: "updated",
              user: rootState.authen.user.email,
            },
            { root: true }
            );
          })
        }
      },
      async callAIAttributeGeneration({commit,state,dispatch,rootState}:any, value:any){
        try {
          const response = await apiNodeRequest({
            method: 'post',
            url: '/openai/get_attribute',
            params: {
              skill: value.skill,
              lang: value.languages,
            },
          })
          return response.data
        } catch (err) {
          const anyErr: any = err
          const status = anyErr && anyErr.response ? anyErr.response.status : undefined
          const body = anyErr && anyErr.response ? anyErr.response.data : undefined
          console.error('[AI attributes] request failed:', err, status, body)
          throw err
        }
      }
      
    },
    getters: {
      setting : (state:any) => {
        return state.settingData
      },
      excels : (state:any) => {
        return state.excels
      },
      recognitionValue: (state:any) => {
        return state.recognitionValue
      },
      hashTagId: (state:any) => {
        return state.hashTagId
      },
      oldRecognitionValue: (state:any) => {
        return state.oldRecognitionValue
      },

      settingOldDataMock: (state:any) => {
        return state.settingOldDataMock
      },
      multiLang: (state:any) => {
        return state.multiLang
      },
      oldExcels: (state:any) => {
        return state.oldExcels
      },
      language: (state:any) => {
        return state.language
      },
      memberinteam: (state:any) => {
       
        return state.memberinteam
      },
      member: (state:any) => {
        
        return state.member
      },
      cohortOptions: (state:any) => {
        return state.cohortOptions
      },
      countryOptions: (state:any) => {
        return state.countryOptions
      },
      presetlist: (state:any) => {
        //console.log("state.presetlist",state.presetlist)
        return state.presetlist
      },
      presetlistAll: (state:any) => {
        return state.presetlistAll
      },
      loading_preset: (state:any) => {
        return state.loading_preset
      },
      townHallThemes: (state:any) => {
        return state.townHallThemes
      },
      excels_detail: (state:any) => {
        return state.excels_detail
      }
    }
  }
